/**
 * The in-app block clipboard (spec §8.18): one entry in IndexedDB `meta`, shared by every tab of the
 * browser profile and available offline; tabs are told about a new copy over a BroadcastChannel. A
 * plain-text outline goes to the system clipboard for other applications.
 */
import { useEffect, useState } from "react";
import type { LoroDoc, TreeID } from "loro-crdt";
import { getMeta, setMeta } from "../../sync/persistence";
import { blocksTree } from "../model";
import { normaliseSelection, outline, snapBlock, type BlockSnap } from "./snapshot";

export interface ClipboardEntry {
  version: 1;
  workspaceId: string;
  sourceDocId: string;
  sourcePageTitle: string;
  copiedAt: number;
  /** `true` for "Copy all blocks": a mirror paste then mirrors the page, not its current roots */
  page: boolean;
  blocks: BlockSnap[];
}

const KEY = "clipboard";
const CHANNEL = "kss-clipboard";
const listeners = new Set<(e: ClipboardEntry | null) => void>();
let channel: BroadcastChannel | null = null;

function ensureChannel(): void {
  if (channel || typeof BroadcastChannel === "undefined") return;
  channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = () => void getMeta<ClipboardEntry>(KEY).then((e) => listeners.forEach((l) => l(e ?? null)));
}

export async function readClipboard(): Promise<ClipboardEntry | null> {
  const e = await getMeta<ClipboardEntry>(KEY);
  return e?.version === 1 ? e : null;
}

/** Copy blocks of a page: the selected roots with their subtrees, or every root for `page`. */
export async function copyBlocks(opts: { doc: LoroDoc; docId: string; workspaceId: string; pageTitle: string; ids?: Iterable<TreeID>; page?: boolean }): Promise<ClipboardEntry> {
  const tree = blocksTree(opts.doc);
  const roots = opts.page ? tree.roots() : normaliseSelection(opts.doc, opts.ids ?? []).map((id) => tree.getNodeByID(id)!).filter(Boolean);
  const entry: ClipboardEntry = {
    version: 1,
    workspaceId: opts.workspaceId,
    sourceDocId: opts.docId,
    sourcePageTitle: opts.pageTitle,
    copiedAt: Date.now(),
    page: !!opts.page,
    blocks: roots.map((n) => snapBlock(opts.docId, n)),
  };
  await setMeta(KEY, entry);
  listeners.forEach((l) => l(entry));
  ensureChannel();
  channel?.postMessage("changed");
  try {
    await navigator.clipboard?.writeText(outline(entry.blocks));
  } catch {
    // no permission or no focus: the in-app clipboard still holds the blocks
  }
  return entry;
}

export function countBlocks(blocks: BlockSnap[]): number {
  return blocks.reduce((n, b) => n + 1 + countBlocks(b.children), 0);
}

/** The current clipboard entry, kept up to date across tabs. */
export function useClipboard(): ClipboardEntry | null {
  const [entry, setEntry] = useState<ClipboardEntry | null>(null);
  useEffect(() => {
    let alive = true;
    ensureChannel();
    void readClipboard().then((e) => alive && setEntry(e));
    const l = (e: ClipboardEntry | null) => alive && setEntry(e);
    listeners.add(l);
    return () => {
      alive = false;
      listeners.delete(l);
    };
  }, []);
  return entry;
}
